import {
  getListRowId,
  collectListSourceHandleIds,
} from "./listRowHandles";

/** Coleta ids brutos das linhas de lista (formato V1: handle = rowId sem prefixo lst_). */
export function collectListRowIdsFromNode(node) {
  const rowIds = new Set();
  if (!node) return rowIds;

  const addFromSections = (sections) => {
    if (!Array.isArray(sections)) return;
    sections.forEach((sec) => {
      (sec?.rows || []).forEach((row) => {
        const id = getListRowId(row);
        if (id) rowIds.add(id);
      });
    });
  };

  if (node.type === "list") {
    addFromSections(node.data?.sections ?? node.data?.data?.sections);
    const listRows = node.data?.listRows ?? node.data?.data?.listRows;
    if (Array.isArray(listRows)) {
      listRows.forEach((row) => {
        const id = getListRowId(row);
        if (id) rowIds.add(id);
      });
    }
    return rowIds;
  }

  if (node.type === "singleBlock") {
    const elements = node.data?.elements ?? node.data?.data?.elements ?? [];
    elements.forEach((el) => {
      if (el?.type === "list") {
        addFromSections(el.sections ?? el.data?.sections);
      }
    });
  }

  return rowIds;
}

/**
 * Flow V1 (listNode): sourceHandle = id da linha (ex: "opt1").
 * Flow V2: sourceHandle = lst_<id> (ex: "lst_opt1").
 */
export function migrateV1ListSourceHandle(sourceNode, sourceHandle) {
  if (!sourceNode || sourceHandle == null || sourceHandle === "") {
    return sourceHandle;
  }

  const h = String(sourceHandle).trim();
  if (!h) return sourceHandle;

  if (sourceNode.type !== "list" && sourceNode.type !== "singleBlock") {
    return sourceHandle;
  }

  const v2Handles = collectListSourceHandleIds(sourceNode);
  if (v2Handles.has(h)) return h;

  const rowIds = collectListRowIdsFromNode(sourceNode);

  // V1 nó lista: handle sem prefixo lst_
  if (sourceNode.type === "list" && !h.startsWith("lst_") && rowIds.has(h)) {
    return `lst_${h}`;
  }

  // lst_ com id que existe nas linhas
  if (h.startsWith("lst_")) {
    const raw = h.slice(4);
    if (rowIds.has(raw)) return `lst_${raw}`;
  }

  return sourceHandle;
}

/**
 * Garante handles de menu/botão no formato a{número} (ex: a1).
 */
export function migrateMenuSourceHandle(sourceNode, sourceHandle) {
  if (!sourceNode || sourceHandle == null || sourceHandle === "") {
    return sourceHandle;
  }

  if (sourceNode.type !== "menu" && sourceNode.type !== "button") {
    return sourceHandle;
  }

  const h = String(sourceHandle).trim();
  if (!h || h.startsWith("a")) return sourceHandle;

  const options =
    sourceNode.data?.arrayOption ??
    sourceNode.data?.data?.arrayOption ??
    [];

  const match = options.find(
    (o) => o != null && String(o.number) === h
  );
  if (match != null) {
    return "a" + match.number;
  }

  return sourceHandle;
}

/** Migra conexões salvas no Flow Builder V1 para handles do V2. */
export function migrateV1FlowConnections(nodes, connections) {
  const nodeById = new Map((nodes || []).map((n) => [n.id, n]));
  let migratedCount = 0;

  const result = (connections || []).map((conn) => {
    const sourceNode = nodeById.get(conn.source);
    if (!sourceNode || conn.sourceHandle == null || conn.sourceHandle === "") {
      return conn;
    }

    let handle = migrateV1ListSourceHandle(sourceNode, conn.sourceHandle);
    handle = migrateMenuSourceHandle(sourceNode, handle);

    if (handle !== conn.sourceHandle) {
      migratedCount += 1;
      return { ...conn, sourceHandle: handle };
    }
    return conn;
  });

  if (migratedCount > 0) {
    console.info(
      `[FlowV2] Migração V1→V2: ${migratedCount} conexão(ões) de lista/menu atualizada(s)`
    );
  }

  return result;
}

function buildValidHandleMaps(nodes) {
  const validTargetHandles = {};
  const validSourceHandles = {};

  const addListHandles = (n) => {
    collectListSourceHandleIds(n).forEach((handleId) => {
      validSourceHandles[n.id].add(handleId);
    });
    collectListRowIdsFromNode(n).forEach((rowId) => {
      validSourceHandles[n.id].add(rowId);
      validSourceHandles[n.id].add(`lst_${rowId}`);
    });
  };

  nodes.forEach((n) => {
    validTargetHandles[n.id] = new Set([undefined, null, ""]);
    validSourceHandles[n.id] = new Set([undefined, null, "", "a"]);

    switch (n.type) {
      case "condition":
      case "conditionCompare":
      case "randomizer":
        validSourceHandles[n.id].add("a");
        validSourceHandles[n.id].add("b");
        break;
      case "menu":
      case "button": {
        const options =
          n.data?.arrayOption ?? n.data?.data?.arrayOption ?? [];
        options.forEach((opt) => {
          validSourceHandles[n.id].add("a" + (opt.number ?? ""));
          if (opt?.number != null) {
            validSourceHandles[n.id].add(String(opt.number));
          }
        });
        break;
      }
      case "list":
        addListHandles(n);
        break;
      case "singleBlock":
        validSourceHandles[n.id].add("btn_1");
        validSourceHandles[n.id].add("btn_2");
        validSourceHandles[n.id].add("btn_3");
        addListHandles(n);
        break;
      case "httpRequest":
        validTargetHandles[n.id].add("httpRequest-in");
        validSourceHandles[n.id].add("httpRequest-out");
        break;
      case "variable":
        validTargetHandles[n.id].add("variable-in");
        validSourceHandles[n.id].add("variable-out");
        break;
      case "ixcBuscarCliente":
      case "ixcBuscarFatura":
        validSourceHandles[n.id].add("success");
        validSourceHandles[n.id].add("not_found");
        validSourceHandles[n.id].add("sem_faturas");
        validSourceHandles[n.id].add("error");
        break;
      case "ixcDesbloquear":
        validSourceHandles[n.id].add("success");
        validSourceHandles[n.id].add("nao_bloqueado");
        validSourceHandles[n.id].add("error");
        break;
      case "ifAdvanced":
        validSourceHandles[n.id].add("true");
        validSourceHandles[n.id].add("false");
        break;
      case "switch":
        (n.data?.cases || []).forEach((_c, i) => {
          validSourceHandles[n.id].add("case_" + i);
        });
        if (n.data?.hasDefault !== false) {
          validSourceHandles[n.id].add("default");
        }
        break;
      case "filter":
        validSourceHandles[n.id].add("pass");
        validSourceHandles[n.id].add("reject");
        break;
      case "stickyNote":
        validSourceHandles[n.id] = new Set();
        validTargetHandles[n.id] = new Set();
        break;
      case "code":
        validSourceHandles[n.id].add("error");
        break;
      case "merge":
        for (let mi = 0; mi < (n.data?.inputCount || 2); mi++) {
          validTargetHandles[n.id].add("merge_in_" + mi);
        }
        break;
      case "loop":
        validSourceHandles[n.id].add("each");
        validSourceHandles[n.id].add("done");
        break;
      case "splitBatch":
        validSourceHandles[n.id].add("batch");
        validSourceHandles[n.id].add("done");
        break;
      case "regex":
        validSourceHandles[n.id].add("match");
        validSourceHandles[n.id].add("noMatch");
        break;
      case "aiAgent":
        validSourceHandles[n.id].add("success");
        validSourceHandles[n.id].add("error");
        break;
      case "textClassifier":
        (n.data?.categories || []).forEach((_c, i) => {
          validSourceHandles[n.id].add("cat_" + i);
        });
        validSourceHandles[n.id].add("fallback");
        break;
      case "stopError":
        validSourceHandles[n.id] = new Set();
        break;
      case "emailSmtp":
        validSourceHandles[n.id].add("sent");
        validSourceHandles[n.id].add("error");
        break;
      case "webhookTrigger":
      case "scheduleTrigger":
      case "errorTrigger":
        validTargetHandles[n.id] = new Set();
        break;
      default:
        break;
    }
  });

  return { validTargetHandles, validSourceHandles };
}

/**
 * Migra conexões V1 e normaliza edges para o editor V2 (sem quebrar vínculos de lista/menu).
 */
export function prepareConnectionsForV2Editor(nodes, connections) {
  const migrated = migrateV1FlowConnections(nodes, connections);
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const { validTargetHandles, validSourceHandles } = buildValidHandleMaps(nodes);

  return migrated.map((c) => {
    const edge = { ...c, type: c.type || "buttonedge" };

    const targetHandles = validTargetHandles[edge.target];
    if (targetHandles && edge.targetHandle && !targetHandles.has(edge.targetHandle)) {
      edge.targetHandle = null;
    }

    const sourceNode = nodeById.get(edge.source);
    const sourceHandles = validSourceHandles[edge.source];

    if (sourceHandles && edge.sourceHandle && !sourceHandles.has(edge.sourceHandle)) {
      const isListSource =
        sourceNode?.type === "list" ||
        (sourceNode?.type === "singleBlock" &&
          (String(edge.sourceHandle).startsWith("lst_") ||
            String(edge.sourceHandle).startsWith("btn_")));

      if (isListSource && sourceNode) {
        const retry = migrateV1ListSourceHandle(sourceNode, edge.sourceHandle);
        if (sourceHandles.has(retry)) {
          edge.sourceHandle = retry;
        }
      } else if (sourceNode?.type !== "list") {
        edge.sourceHandle = "a";
      }
    }

    return edge;
  });
}
